import { Pagination, ToastContainer } from "pepsico-ds";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonTableView from "../../components/common/CommonTable/CommonTableView";
import {
  ListingComponentContext,
  ListingComponentProvider,
} from "../../components/common/listingComponents/ListingComponentContext";
import ListingComponentWrapper from "../../components/common/listingComponents/ListingComponentWrapper";
import NoResultsFound from "../../components/common/table/NoResultsFound";
import { FileHistoryButton } from "../../components/fileManager";
import ConfirmationModal from "../../components/performances/ConfirmationModal";
import PerformanceDetailsDrawer from "../../components/performances/PerformanceDetailsDrawer";
import { GlobalConfigContext } from "../../context/GlobalConfigContext";
import LoaderContext from "../../context/LoaderContext";
import { listEngagements } from "../../graphql/queries/listEngagements";
import { useEngagementActions } from "../../hooks/useEngagementActions";
import { paths } from "../../routes/paths";
import {
  receiptScanPerformancesFilterAllJsonSchema,
  receiptScanPerformancesFilterAllUiSchema,
  receiptScanPerformancesFilterJsonSchema,
  receiptScanPerformancesFilterUiSchema,
} from "./mocks";

const contentDefinition = {
  views_config: {
    table: {
      column_config: {
        id: {
          label: "ID",
          render: (value) => (
            <a
              href="#"
              className="performances-widget-id"
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              {value}
            </a>
          ),
        },
        status: { type: "statusBadge", headerClass: "status" },
        performanceDate: { type: "date" },
        receiptDate: {
          label: "Receipt Date",
          type: "date",
          valuePath: "metadata.receiptData.receipt_data.bill_date",
        },
        profileLogin: { label: "User ID" },
        profileId: { label: "Profile ID" },
        channel: { label: "Channel" },
        image: { type: "image", headerClass: "user" },
        identifier: { label: "External ID" },
        vendorName: { label: "Retailer" },
        outcomes: {
          flatten: true,
          joinArrayValues: true,
          fields: [
            { key: "outcome", label: "Outcome Name" },
            { key: "value", label: "Outcome Quantity" },
          ],
        },
        outcomeName: {
          label: "Outcome Name",
          arrayField: "outcome",
          joinField: "outcome",
          type: "arrayJoin",
        },
        outcomeQuantity: {
          label: "Outcome Quantity",
          arrayField: "value",
          joinField: "value",
          type: "arrayJoin",
        },
        fraudScore: {
          label: "Fraud Score",
          type: "number",
          valuePath: "metadata.receiptData.fraud.score",
        },
      },
    },
  },
};

const ReceiptScanPerformance = () => {
  const ROWS_PER_PAGE = 10;
  return (
    <ListingComponentProvider
      query={listEngagements}
      fetchPolicy="cache-and-network"
      queryKey="listEngagements"
      initialFilterData={{
        engagementStatus: [],
        engagementType: ["receipt-scan"],
        limit: ROWS_PER_PAGE,
        offset: 0,
      }}
      noPagination={true}
    >
      <ReceiptScanPerformanceContent rowsPerPage={ROWS_PER_PAGE} />
    </ListingComponentProvider>
  );
};

const ReceiptScanPerformanceContent = ({ rowsPerPage }) => {
  const {
    viewableRecords,
    filterData,
    setFilterData,
    setViewAllFilters,
    handleClearFilters,
    refetch,
  } = useContext(ListingComponentContext);
  const { programTimezone, globalState } = useContext(GlobalConfigContext) || {};
  const programId = localStorage.getItem("programId") || "";
  const { setIsLoading } = useContext(LoaderContext);
  const navigate = useNavigate();

  const [toastData, setToastData] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [performanceID, setPerformanceID] = useState(null);
  const [engagementItem, setEngagementItem] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const handleModalClose = () => {
    setModalType(null);
    setEngagementItem(null);
  };

  const { handleEngagementAction } = useEngagementActions({
    setIsLoading,
    setToastData,
    handleModalClose,
    refetch,
    programId,
    globalState,
  });

  useEffect(() => {
    if (filterData) {
      setCurrentPage(Math.ceil(filterData.offset / rowsPerPage) + 1);
    }
  }, [filterData, rowsPerPage]);

  const handleDetailsViewClick = (performanceID) => {
    navigate(`../${paths.performance_details}/${performanceID}`);
  };

  const handleModalOpen = (type) => setModalType(type);

  const handleOnTap = (item) => {
    setPerformanceID(item.id);
    setEngagementItem(item);
    setShowDrawer(true);
  };

  const handleChangePage = (page) => {
    setFilterData((prev) => ({
      ...prev,
      offset: (page - 1) * rowsPerPage,
    }));
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(viewableRecords?.total / rowsPerPage);

  const onActionClick = (action, data) => {
    handleEngagementAction(action, engagementItem, data);
  };

  return (
    <div className="px-3 pt-3">
      {toastData && (
        <ToastContainer
          className="performances-listing-toast"
          data={toastData}
          removeToast={() => setToastData([])}
          showActionIcon
        />
      )}
      <div className="container-card">
        <div className="performances-page-content">
          <div className="display-flex align-items-center justify-content-space-between">
            <div className="performances-header">
              <h2>Receipt Scan Performances</h2>
              <span className="total-results-count">
                {Number(viewableRecords?.total || 0).toLocaleString()}{" "}
                {`${Number(viewableRecords?.total) > 1 ? "Results" : "Result"}`}
              </span>
            </div>
            <FileHistoryButton contentType="text/csv" variant="secondary" />
          </div>
          <ListingComponentWrapper
            customStyles={{ flex: 1 }}
            filterClasses="performances-filter-styles all-debounced-nondebounced-style"
            filterConfig={{
              withDebounce: {
                jsonSchema: receiptScanPerformancesFilterJsonSchema.withDebounce,
                uiSchema: receiptScanPerformancesFilterUiSchema.withDebounce,
              },
              withoutDebounce: {
                jsonSchema: receiptScanPerformancesFilterJsonSchema.withoutDebounce,
                uiSchema: receiptScanPerformancesFilterUiSchema.withoutDebounce,
              },
              allFilters: {
                jsonSchema: receiptScanPerformancesFilterAllJsonSchema,
                uiSchema: receiptScanPerformancesFilterAllUiSchema,
              },
            }}
            toggleViewType={false}
            showAllFiltersOption={true}
            exportOptions={true}
            exportOptionsConfig={{
              context: "RECEIPT_SCAN_PERFORMANCES",
              filename: `receipt_scan_performances_${Date.now()}.csv`,
            }}
            offsetBasedPagination={true}
          >
            <CommonTableView
              contentList={viewableRecords?.items || []}
              columnConfig={contentDefinition.views_config.table.column_config}
              programTimezone={programTimezone}
              excludeColumns={[
                "image",
                "profileEmail",
                "profilePhone",
                "engagementType",
                "productSkus",
                "programPoints",
                "campaignPoints",
                "outcomes",
                "metadata",
                "deviceId",
              ]}
              noResultsFound={
                <NoResultsFound
                  onChangeFilters={() => setViewAllFilters(true)}
                  onResetAllFilters={() => handleClearFilters()}
                />
              }
              cellClickHandlers={{ id: (row) => handleOnTap(row) }}
            />
            {totalPages > 1 && (
              <div className="pagination-wrap mb-4">
                <Pagination
                  currentPage={currentPage}
                  onUpdate={handleChangePage}
                  pageCount={totalPages}
                  size="medium"
                  variant="number"
                />
              </div>
            )}
          </ListingComponentWrapper>
        </div>
      </div>

      {engagementItem && (
        <PerformanceDetailsDrawer
          showDrawer={showDrawer}
          engagementItem={engagementItem}
          onCloseDrawer={() => {
            setShowDrawer(false);
            setEngagementItem(null);
          }}
          onDetailViewClick={() => handleDetailsViewClick(performanceID)}
          onApprove={() => {
            setShowDrawer(false);
            handleModalOpen("approve");
          }}
          onReject={() => {
            setShowDrawer(false);
            handleModalOpen("reject");
          }}
        />
      )}
      {modalType === "approve" && (
        <ConfirmationModal
          showModal={modalType === "approve"}
          onClose={handleModalClose}
          title="Approve"
          onConfirm={(data) => onActionClick("approve", data)}
        />
      )}

      {modalType === "reject" && (
        <ConfirmationModal
          showModal={modalType === "reject"}
          onClose={handleModalClose}
          title="Reject"
          onConfirm={(data) => onActionClick("reject", data)}
        />
      )}
    </div>
  );
};

export default ReceiptScanPerformance;
